import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Globe, 
  FileText, 
  Calendar,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Hash,
  CreditCard,
  Building
} from 'lucide-react';
import { Customer, customerService } from '../../../services/customerService';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import { formatDistanceToNow } from 'date-fns';

// Brand colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444", 
  errorLight: "#FEF2F2",
};

export default function CustomerDetails() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerId) {
        setError('Customer ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await customerService.getCustomerById(customerId);
        setCustomer(response.data);
      } catch (err: any) {
        console.error('Failed to fetch customer:', err);
        setError(err.message || 'Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId]);

  const handleEdit = () => {
    navigate('/masters/customers', { state: { editCustomer: customer } });
  };

  const handleDelete = async () => {
    if (!customer || !window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      await customerService.inactivateCustomer(customer._id);
      navigate('/masters/customers');
    } catch (err: any) {
      setError(err.message || 'Failed to delete customer');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageBreadcrumb 
          pageTitle="Customer Details" 
          breadcrumbs={[
            { name: 'Customers', path: '/masters/customers' },
            { name: 'Details', path: '#' }
          ]}
        />
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error || 'Customer not found'}
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'inactive':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="text-green-600" />;
      case 'inactive':
        return <XCircle size={16} className="text-red-600" />;
      default:
        return <Hash size={16} className="text-gray-500" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <PageBreadcrumb 
        pageTitle="Customer Details" 
        breadcrumbs={[
          { name: 'Customers', path: '/masters/customers' },
          { name: customer.name, path: '#' }
        ]}
      />

      {/* Back Button */}
      <button
        onClick={() => navigate('/masters/customers')}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
      >
        <ArrowLeft size={16} />
        Back to Customers
      </button>

      {/* Customer Details Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-lg border border-gray-200 overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg flex items-center justify-center border"
                style={{ 
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                  borderColor: BRAND_COLORS.primary
                }}
              >
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-sm text-gray-600">Customer ID: {customer.code}</span>
                  <div className={`inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-md border ${getStatusColor(customer.status)}`}>
                    {getStatusIcon(customer.status)}
                    {customer.status.charAt(0).toUpperCase() + customer.status.slice(1)}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleEdit}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-lg transition-all duration-200"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-500 border border-transparent rounded-lg hover:bg-red-600 transition-all duration-200"
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 size={20} style={{ color: BRAND_COLORS.primary }} />
                  Basic Information
                </h3>
                <div className="space-y-4">
                  {customer.companyName && (
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-600">Company Name</span>
                        <p className="text-sm font-medium text-gray-900">{customer.companyName}</p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Hash className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-600">Customer Type</span>
                      <p className="text-sm font-medium text-gray-900">{customer.customerType}</p>
                    </div>
                  </div>
                  {customer.industry && (
                    <div className="flex items-center gap-3">
                      <Building className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-600">Industry</span>
                        <p className="text-sm font-medium text-gray-900">{customer.industry}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Mail size={20} style={{ color: BRAND_COLORS.primary }} />
                  Contact Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-600">Email</span>
                      <p className="text-sm font-medium text-gray-900">{customer.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-600">Phone</span>
                      <p className="text-sm font-medium text-gray-900">{customer.phone}</p>
                    </div>
                  </div>
                  {customer.mobile && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-600">Mobile</span>
                        <p className="text-sm font-medium text-gray-900">{customer.mobile}</p>
                      </div>
                    </div>
                  )}
                  {customer.website && (
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-gray-400" />
                      <div>
                        <span className="text-sm text-gray-600">Website</span>
                        <a 
                          href={customer.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm font-medium hover:underline"
                          style={{ color: BRAND_COLORS.primary }}
                        >
                          {customer.website}
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Address and Tax Information */}
            <div className="space-y-6">
              {/* Address */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin size={20} style={{ color: BRAND_COLORS.primary }} />
                  Address Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm text-gray-600">Address</span>
                      <div className="text-sm font-medium text-gray-900 space-y-1">
                        <p>{customer.address.street}</p>
                        <p>{customer.address.city}, {customer.address.state}</p>
                        <p>{customer.address.country} - {customer.address.pincode}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Information */}
              {(customer.gstNumber || customer.panNumber || customer.registrationNumber) && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard size={20} style={{ color: BRAND_COLORS.primary }} />
                    Tax Information
                  </h3>
                  <div className="space-y-4">
                    {customer.gstNumber && (
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm text-gray-600">GST Number</span>
                          <p className="text-sm font-medium text-gray-900 font-mono">{customer.gstNumber}</p>
                        </div>
                      </div>
                    )}
                    {customer.panNumber && (
                      <div className="flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm text-gray-600">PAN Number</span>
                          <p className="text-sm font-medium text-gray-900 font-mono">{customer.panNumber}</p>
                        </div>
                      </div>
                    )}
                    {customer.registrationNumber && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <span className="text-sm text-gray-600">Registration Number</span>
                          <p className="text-sm font-medium text-gray-900">{customer.registrationNumber}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar size={20} style={{ color: BRAND_COLORS.primary }} />
                  System Information
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-600">Created</span>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDistanceToNow(new Date(customer.createdAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <span className="text-sm text-gray-600">Last Updated</span>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDistanceToNow(new Date(customer.updatedAt), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {customer.notes && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText size={20} style={{ color: BRAND_COLORS.primary }} />
                Notes
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 leading-relaxed">{customer.notes}</p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
